import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack, Tab, Tabs } from '@mui/material';
import { t } from 'i18next';
import React, { FC, useContext, useState } from 'react';

import { BotContext } from '../../telegram-bots/bot.context';
import { TelegramMessengerAccess } from '../../telegram-bots/telegram-bot.interface';
import { UserContext } from '../../user/user.context';
import { IMessengerTab } from '../messenger.page';

import { ITemplate } from './interfaces';
import TemplateCard from './template-card';

interface TemplateListProps {
  templates: ITemplate[];
  openModal: () => void;
  onEdit: (template: Partial<ITemplate>) => void;
  onDelete: (_id: ITemplate['_id']) => void;
}

interface TemplatePanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TemplateTabPanel(props: TemplatePanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Stack role="tabpanel" hidden={value !== index} {...other}>
      {value === index && (
        <Box sx={{ width: '100%', maxWidth: '95%', margin: '0 auto' }}>
          {children}
        </Box>
      )}
    </Stack>
  );
}

const TemplateList: FC<TemplateListProps> = ({
  templates,
  openModal,
  onEdit,
  onDelete,
}) => {
  const { user } = useContext(UserContext);
  const { access } = useContext(BotContext);

  const [value, setValue] = useState<number>(0);

  const handleChangeTabIndex = (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setValue(newValue);
  };

  const TABS: IMessengerTab[] = [
    {
      label: t('messenger.templates.generalLabel'),
    },
    {
      label: t('messenger.templates.privateLabel'),
    },
  ];

  return (
    <Stack
      sx={{
        margin: '0 auto',
        height: '100%',
        width: '100%',
      }}
    >
      <Box sx={{ width: '100%', height: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            onChange={handleChangeTabIndex}
            sx={{
              backgroundColor: 'grey.15',
              '& .MuiTabs-indicator': {
                backgroundColor: 'green.2',
              },
              '& .MuiTab-root.Mui-selected': {
                color: 'green.2',
              },
            }}
          >
            {TABS.map((TAB, index) => (
              <Tab
                key={index}
                label={TAB.label}
                sx={{
                  width: '50%',
                  maxWidth: 'none',
                  '&:first-child': {
                    borderRight: '1px solid ',
                    borderColor: 'grey.10',
                  },
                }}
              />
            ))}
          </Tabs>
        </Box>
        <>
          {TABS.map((TAB, index) => {
            return (
              <TemplateTabPanel key={index} value={value} index={index}>
                <Stack
                  flexDirection="row"
                  flexWrap="wrap"
                  gap="28px"
                  sx={{
                    padding: '20px 20px 10px',
                    margin: '20px auto 0',
                    maxHeight: 'calc(100vh - 170px)',
                    overflow: 'auto',
                    width: '100%',
                  }}
                >
                  {templates
                    .filter((template) =>
                      TAB.label === t('messenger.templates.privateLabel')
                        ? template.isPrivate &&
                          template.owner.includes(user?._id ?? '')
                        : !template.isPrivate,
                    )
                    ?.map(
                      ({
                        _id,
                        name,
                        text,
                        buttons,
                        media,
                        isPrivate,
                        type,
                        owner,
                      }) => (
                        <TemplateCard
                          key={_id}
                          title={name}
                          media={media}
                          text={text}
                          buttons={buttons}
                          onEdit={() => {
                            onEdit({
                              _id,
                              name,
                              media,
                              text,
                              buttons,
                              isPrivate,
                              type,
                            });
                          }}
                          isDisabledDelete={
                            Boolean(!owner.includes(user?._id ?? '')) &&
                            Boolean(
                              access.messenger !==
                                TelegramMessengerAccess.admin,
                            )
                          }
                          onDelete={() => onDelete(_id)}
                        />
                      ),
                    )}
                </Stack>
              </TemplateTabPanel>
            );
          })}
        </>
      </Box>
      <Button
        className="brightGreen"
        sx={{
          width: '100%',
          mt: 'auto',
          mx: '16px ',
          mb: '24px',
          height: '48px',
          borderRadius: ' 6px',
        }}
        onClick={openModal}
      >
        <AddIcon sx={{ width: '24px', height: '24px', marginRight: '10px' }} />
        {t('messenger.templates.createTemplateBtn')}
      </Button>
    </Stack>
  );
};

export default TemplateList;
